import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody } from 'reactstrap';
import classNames from 'classnames/bind';

import styles from './ModalSellerWarning.module.scss';

const cx = classNames.bind(styles);

const PROHIBITED_ACTIONS = [
  'Cho mượn, cho thuê, chuyển nhượng, mua bán hoặc chia sẻ tài khoản cho bên thứ ba trái quy định.',
  'Sử dụng tài khoản để đăng tải, kinh doanh hoặc cung cấp sản phẩm/dịch vụ thuộc danh mục bị cấm hoặc hạn chế theo quy định.',
  'Đăng tải thông tin sai sự thật, gây nhầm lẫn về sản phẩm, dịch vụ, giá bán, chương trình khuyến mại hoặc quyền lợi của khách hàng.',
  'Thực hiện các hành vi gian lận như tạo giao dịch giả, đơn hàng giả, đánh giá giả, lợi dụng chương trình khuyến mại, hoàn tiền, tích điểm hoặc các chính sách của hệ thống.',
  'Sử dụng công cụ, phần mềm hoặc phương thức kỹ thuật nhằm can thiệp trái phép vào hệ thống, dữ liệu hoặc hoạt động của người dùng khác.',
  'Thu thập, sử dụng hoặc tiết lộ thông tin của khách hàng trái với mục đích được phép hoặc quy định về bảo vệ dữ liệu cá nhân.',
  'Thực hiện các hành vi khác vi phạm Điều khoản sử dụng, Quy chế hoạt động của nền tảng hoặc quy định pháp luật hiện hành.',
];

function ModalSellerWarning(props) {
  const {
    isOpen,
    onCloseModal,
    onConfirm,
  } = props;

  const [isAgreed, setIsAgreed] = useState(false);

  const handleCheckboxChange = useCallback((e) => {
    setIsAgreed(e.target.checked);
  }, []);

  const handleConfirm = useCallback(() => {
    if (isAgreed && onConfirm) {
      onConfirm();
    }
  }, [isAgreed, onConfirm]);

  return (
    <Modal
      className={cx('ModalOverlay')}
      isOpen={isOpen}
      toggle={onCloseModal}
      contentClassName={cx('ModalContent')}
      centered
      backdrop="static"
    >
      {/* Header */}
      <div className={cx('ModalHeader')}>
        <div className={cx('HeaderIcon')}>
          <i className="fas fa-exclamation-triangle" />
        </div>
        <div className={cx('HeaderContent')}>
          <h4 className={cx('HeaderTitle')}>
            CẢNH BÁO VỀ VIỆC SỬ DỤNG TÀI KHOẢN NGƯỜI BÁN
          </h4>
          <p className={cx('HeaderSubtitle')}>
            Về các hành vi không được thực hiện khi tạo &amp; sử dụng tài khoản
          </p>
        </div>
        <button
          type="button"
          className={cx('BtnClose')}
          onClick={onCloseModal}
          aria-label="Close"
        >
          <i className="fas fa-times" />
        </button>
      </div>

      {/* Body */}
      <ModalBody className={cx('ModalBody')}>
        <div className={cx('ScrollArea')}>
          {/* Note / Objective Box */}
          <div className={cx('AlertBox')}>
            <div className={cx('AlertBoxIcon')}>
              <i className="fas fa-shield-alt" />
            </div>
            <div className={cx('AlertBoxContent')}>
              <div className={cx('AlertBoxTitle')}>
                CHỦ TRƯƠNG BẢO VỆ NGƯỜI DÙNG &amp; TÍNH MINH BẠCH CỦA SÀN THƯƠNG MẠI ĐIỆN TỬ
              </div>
              <div className={cx('AlertBoxText')}>
                Người bán có trách nhiệm quản lý và sử dụng tài khoản đúng mục đích,
                tuân thủ quy định của nền tảng và quy định pháp luật có liên quan.
              </div>
            </div>
          </div>

          {/* Section 1: Prohibited Actions */}
          <div className={cx('SectionCard')}>
            <div className={cx('SectionHeader')}>
              <i className="fas fa-user-slash text-danger me-2" />
              <span className={cx('SectionTitle')}>
                Các hành vi không được phép thực hiện:
              </span>
            </div>
            <div className={cx('SectionBody')}>
              <ul className={cx('ProhibitedList')}>
                {PROHIBITED_ACTIONS.map((item, index) => (
                  <li key={item} className={cx('ProhibitedItem')}>
                    <span className={cx('ItemIndex')}>
                      {index + 1}
                      .
                    </span>
                    <span className={cx('ItemText')}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Section 2: Penalty / Violation Handling */}
          <div className={cx('SectionCard', 'PenaltyCard')}>
            <div className={cx('SectionHeader')}>
              <i className="fas fa-gavel text-danger me-2" />
              <span className={cx('SectionTitle')}>
                Xử lý vi phạm:
              </span>
            </div>
            <div className={cx('SectionBody')}>
              <p className={cx('PenaltyText')}>
                Tùy theo mức độ vi phạm, hệ thống có quyền áp dụng các biện pháp
                như cảnh báo, hạn chế chức năng, tạm khóa/khóa tài khoản,
                hủy giao dịch, thu hồi quyền lợi hoặc thực hiện các biện pháp
                xử lý khác theo quy định của nền tảng và pháp luật.
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation Footer Section */}
        <div className={cx('ConfirmationSection')}>
          <div className={cx('NoticeBar')}>
            <i className="fas fa-check-circle me-1" />
            Đã đọc hết nội dung cảnh báo. Vui lòng tích chọn cam kết bên dưới.
          </div>

          <div className={cx('CheckboxWrapper')}>
            <label className={cx('CheckboxLabel')} htmlFor="seller-warning-agree">
              <input
                id="seller-warning-agree"
                type="checkbox"
                className={cx('CheckboxInput')}
                checked={isAgreed}
                onChange={handleCheckboxChange}
              />
              <span className={cx('CustomCheckbox')}>
                <i className="fas fa-check" />
              </span>
              <span className={cx('CheckboxText')}>
                Tôi xác nhận đã đọc, hiểu đầy đủ nội dung cảnh báo trên
                và cam kết tuân thủ các quy định khi sử dụng tài khoản.
              </span>
            </label>
          </div>

          <button
            type="button"
            className={cx('BtnSubmit', { 'BtnSubmit--disabled': !isAgreed })}
            disabled={!isAgreed}
            onClick={handleConfirm}
          >
            <i className="fas fa-check-square me-2" />
            Xác nhận và tiếp tục
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

ModalSellerWarning.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func,
  onConfirm: PropTypes.func,
};

ModalSellerWarning.defaultProps = {
  onCloseModal: () => {},
  onConfirm: () => {},
};

export default React.memo(ModalSellerWarning);
